import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { companiesApi } from "./api";
import { CompanyProfileFields, splitJobTypes } from "./CompanyFormFields";
import { useIndustryOptions } from "../industries/api";
import { Button, Card, Field, Input, PageTitle } from "../../components/ui";
import { ApiError } from "../../lib/api";

interface FormValues {
  name: string;
  area: string;
  industryCode: string;
  jobTypesText: string;
  description: string;
  capacity: number;
  conditions: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  latitude?: number | null;
  longitude?: number | null;
  loginEmail: string;
  password: string;
}

export function CompanyRegisterPage() {
  const [done, setDone] = useState(false);
  const industryQuery = useIndustryOptions();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: { conditions: "", industryCode: "" } });

  const mutation = useMutation({
    mutationFn: (v: FormValues) =>
      companiesApi.register({
        name: v.name,
        area: v.area,
        industryCode: v.industryCode,
        jobTypes: splitJobTypes(v.jobTypesText),
        description: v.description,
        capacity: Number(v.capacity),
        conditions: v.conditions ?? "",
        contactName: v.contactName,
        contactEmail: v.contactEmail,
        contactPhone: v.contactPhone,
        latitude: v.latitude ?? null,
        longitude: v.longitude ?? null,
        loginEmail: v.loginEmail,
        password: v.password,
      }),
    onSuccess: () => setDone(true),
  });

  if (done) {
    return (
      <Card className="mx-auto max-w-xl text-center">
        <PageTitle title="登録を受け付けました" />
        <p className="text-sm text-slate-600">
          苫小牧タウンマネジメントの審査後、受入企業として公開されます。承認結果はメールでお知らせします。
        </p>
        <div className="mt-6">
          <Link to="/">
            <Button variant="ghost">トップにもどる</Button>
          </Link>
        </div>
      </Card>
    );
  }

  const apiErr = mutation.error instanceof ApiError ? mutation.error.message : null;

  return (
    <div className="mx-auto max-w-2xl">
      <PageTitle
        title="受入企業として登録"
        subtitle="中学生の職場体験を受け入れる企業を募集しています。"
      />
      <Card>
        <form className="space-y-4" onSubmit={handleSubmit((v) => mutation.mutate(v))}>
          <CompanyProfileFields
            register={register}
            errors={errors}
            industry={{
              isLoading: industryQuery.isLoading,
              isError: industryQuery.isError,
              data: industryQuery.data,
              refetch: () => void industryQuery.refetch(),
            }}
            location={{
              value: { latitude: watch("latitude") ?? null, longitude: watch("longitude") ?? null },
              onChange: (v) => {
                setValue("latitude", v?.latitude ?? null);
                setValue("longitude", v?.longitude ?? null);
              },
            }}
          />

          <div className="border-t border-sky-base/60 pt-4">
            <p className="mb-2 text-sm font-bold text-slate-600">ログインアカウント</p>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="ログイン用メール" error={errors.loginEmail?.message}>
                <Input
                  type="email"
                  {...register("loginEmail", { required: "ログイン用メールは必須です" })}
                />
              </Field>
              <Field label="パスワード（12文字以上）" error={errors.password?.message}>
                <Input
                  type="password"
                  {...register("password", {
                    required: "パスワードは必須です",
                    minLength: { value: 12, message: "12文字以上にしてください" },
                  })}
                />
              </Field>
            </div>
          </div>

          {apiErr && <p className="text-sm font-bold text-rose-500">{apiErr}</p>}
          <div className="flex justify-end gap-2">
            <Link to="/">
              <Button variant="ghost" type="button">
                キャンセル
              </Button>
            </Link>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                mutation.isPending ||
                industryQuery.isLoading ||
                industryQuery.isError ||
                industryQuery.data?.length === 0 // 業種0件は送信 disabled（§3.3・空 code の 400 を防ぐ）
              }
            >
              登録する
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
